'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export interface Notification {
    id: number;
    titulo: string;
    mensagem: string;
    lida: boolean;
    data: string;
    dados?: string | any;
    imagem?: string;
    capa?: string;
}

interface PaginationData {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface NotificationsContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    pagination: PaginationData | null;
    fetchNotifications: (page?: number, type?: string) => Promise<void>;
    fetchUnreadCount: () => Promise<void>;
    markAsRead: (id: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    deleteReadNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const fetchCountTriggeredRef = useRef(false);

    const fetchNotifications = useCallback(async (page = 1, type?: string) => {
        if (!isAuthenticated) return;

        try {
            setLoading(true);
            const typeQuery = type ? `&tipo=${type}` : '';
            const response = await api.get(`notificacoes?pagina=${page}&limite=20${typeQuery}`, { requiresAuth: true });

            let data: Notification[] = [];
            let paginationData: PaginationData | null = null;
            let unreadFromApi: number | null = null;

            const payload = response.data as {
                data?: Notification[];
                pagination?: PaginationData;
                nao_lidas?: number;
            } | Notification[];

            if (payload && typeof payload === 'object' && !Array.isArray(payload) && payload.data) {
                data = payload.data;
                paginationData = payload.pagination ?? null;
                unreadFromApi = typeof payload.nao_lidas === 'number' ? payload.nao_lidas : null;
            } else if (Array.isArray(payload)) {
                data = payload;
            }

            if (page === 1) {
                setNotifications(data);
            } else {
                setNotifications(prev => [...prev, ...data]);
            }

            setPagination(paginationData);
            if (unreadFromApi != null) {
                setUnreadCount(unreadFromApi);
                localStorage.setItem('notificationsCount', String(unreadFromApi));
            }
        } catch (error) {
            console.error('Erro ao buscar notificações:', error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    const fetchUnreadCount = useCallback(async () => {
        if (!isAuthenticated) return;

        const storedCount = localStorage.getItem('notificationsCount');
        if (storedCount !== null) {
            const count = Number(storedCount);
            if (!isNaN(count)) {
                setUnreadCount(count);
                setTimeout(async () => {
                    try {
                        const response = await api.get('notificacoes?pagina=1&limite=1', { requiresAuth: true, cacheTtlSeconds: 30 });
                        const payload = response.data as { nao_lidas?: number };
                        const numCount = Number(payload?.nao_lidas) || 0;
                        setUnreadCount(numCount);
                        localStorage.setItem('notificationsCount', String(numCount));
                    } catch (err: any) {
                        if (err?.status === 401 || err?.isAuthError) return;
                    }
                }, 2000);
                return;
            }
        }

        try {
            const response = await api.get('notificacoes?pagina=1&limite=1', { requiresAuth: true, cacheTtlSeconds: 30 });
            const payload = response.data as { nao_lidas?: number };
            const numCount = Number(payload?.nao_lidas) || 0;
            setUnreadCount(numCount);
            localStorage.setItem('notificationsCount', String(numCount));
        } catch (error: any) {
            if (error?.status === 401 || error?.isAuthError) return;
            console.error('Erro ao buscar contagem de não lidas:', error);
        }
    }, [isAuthenticated]);

    const markAsRead = useCallback(async (id: number) => {
        try {
            await api.patch(`notificacoes/${id}/marcar-como-lida`, {}, { requiresAuth: true });

            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, lida: true } : n)
            );

            setUnreadCount(prev => {
                const newCount = Math.max(0, prev - 1);
                localStorage.setItem('notificationsCount', String(newCount));
                return newCount;
            });
        } catch (error) {
            console.error('Erro ao marcar como lida:', error);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await api.patch('notificacoes/marcar-todas-como-lidas', {}, { requiresAuth: true });

            setNotifications(prev =>
                prev.map(n => ({ ...n, lida: true }))
            );
            setUnreadCount(0);
            localStorage.setItem('notificationsCount', '0');
        } catch (error) {
            console.error('Erro ao marcar todas como lidas:', error);
        }
    }, []);
    const deleteReadNotifications = useCallback(async () => {
        try {
            await api.delete('notificacoes/lidas', { requiresAuth: true });

            setNotifications(prev => prev.filter(n => !n.lida));
        } catch (error) {
            console.error('Erro ao excluir notificações lidas:', error);
        }
    }, []);

    // Fetch inicial só após auth ter validado (evita 401 + loop quando sessão inválida)
    useEffect(() => {
        if (authLoading) return;
        if (isAuthenticated && !fetchCountTriggeredRef.current) {
            fetchCountTriggeredRef.current = true;
            fetchUnreadCount();
        }
        if (!isAuthenticated) {
            fetchCountTriggeredRef.current = false;
        }
    }, [authLoading, isAuthenticated, fetchUnreadCount]);

    const value: NotificationsContextType = {
        notifications,
        unreadCount,
        loading,
        pagination,
        fetchNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteReadNotifications,
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    const ctx = useContext(NotificationsContext);
    if (ctx === undefined) {
        throw new Error('useNotifications deve ser usado dentro de NotificationsProvider');
    }
    return ctx;
}
